import { useEffect, useRef } from 'react';
import { hashKey, QueryKey } from '@tanstack/react-query';

type QueryEffectsSource<TData, TError> = {
  data: TData | undefined;
  error: TError | null;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
  isFetchedAfterMount: boolean;
};

export type QueryEffects<TData, TError> = {
  onSuccess?: (data: TData) => unknown;
  onError?: (error: TError) => unknown;
  onSettled?: (data: TData | undefined, error: TError | null) => unknown;
};

type Stamp = { dataUpdatedAt: number; errorUpdatedAt: number };

const stampOf = ({ dataUpdatedAt, errorUpdatedAt }: Stamp): Stamp => ({ dataUpdatedAt, errorUpdatedAt });

/**
 * @deprecated A migration bridge for the useQueries onSuccess/onError/onSettled options that TanStack Query v5
 * removed, not a pattern for new code: derive state from the query result, or run side effects in the mutation or
 * event that causes them. Like v3, the callbacks run once per mounted observer for every fetch of its current key
 * that settles. isFetchedAfterMount resets when the key changes, so switching onto a key with cached data or a
 * cached error does not count as a settled fetch.
 */
export function useQueriesEffects_deprecated<TData, TError>(
  queries: readonly QueryEffectsSource<TData, unknown>[],
  effects: readonly (QueryEffects<TData, TError> & { queryKey: QueryKey })[],
): void {
  const latest = useRef({ queries, effects });
  latest.current = { queries, effects };
  const identityOf = (index: number) => hashKey(latest.current.effects[index].queryKey);
  const stampsById = (current: readonly QueryEffectsSource<TData, unknown>[]) =>
    new Map(current.map((query, index) => [identityOf(index), stampOf(query)]));
  const seen = useRef<Map<string, Stamp> | null>(null);
  seen.current ??= stampsById(queries);
  const signature = queries.map((q) => `${q.dataUpdatedAt}:${q.errorUpdatedAt}:${q.isFetchedAfterMount}`).join();

  useEffect(() => {
    const { queries: current, effects: callbacks } = latest.current;
    current.forEach((query, index) => {
      const previous = seen.current?.get(identityOf(index)) ?? query;
      if (!query.isFetchedAfterMount) return;
      if (query.dataUpdatedAt !== previous.dataUpdatedAt && query.dataUpdatedAt > 0) {
        callbacks[index]?.onSuccess?.(query.data as TData);
        callbacks[index]?.onSettled?.(query.data, null);
      }
      if (query.errorUpdatedAt !== previous.errorUpdatedAt && query.error != null) {
        callbacks[index]?.onError?.(query.error as TError);
        callbacks[index]?.onSettled?.(undefined, query.error as TError);
      }
    });
    seen.current = stampsById(current);
  }, [signature]);
}

/**
 * @deprecated A migration bridge for the useQuery onSuccess/onError/onSettled options that TanStack Query v5 removed,
 * not a pattern for new code. See useQueriesEffects_deprecated.
 */
export function useQueryEffects_deprecated<TData, TError>(
  query: QueryEffectsSource<TData, TError>,
  effects: QueryEffects<TData, TError>,
): void {
  useQueriesEffects_deprecated([query], [{ ...effects, queryKey: [] }]);
}
